import { createSlice, type PayloadAction } from '@reduxjs/toolkit'
import type { IncomeVerification, BorrowerInfo, IncomeVerificationPhase } from '../../types/incomeVerification'
import type { DocumentClassification, DocumentScanStatus, ComplianceResult } from '../../types/documentScanning'

export interface ViewingDocument {
  documentId: string
  documentUrl?: string
}

// Document scanning state
export interface DocumentScanState {
  status: DocumentScanStatus
  workflowId: string | null
  classifications: DocumentClassification[]
  complianceResult: ComplianceResult | null
  progress: number
  error: string | null
  // Extended fields for runtime updates
  phase?: string
  message?: string
  documents?: Record<string, unknown>
  stats?: {
    total_documents: number
    parsed_documents: number
    classified_documents: number
    user_edited_count: number
  }
  updatedAt?: number
  // User edit tracking (prevents webhook overwrites)
  userEdited?: boolean
}

export interface CaseSummary {
  overview: string
  keyFindings: string[]
  recommendations: string[]
  nextSteps: string[]
}

export interface CaseRuntime {
  status: string
  phase: string
  progress: number
  message: string
  data?: any
  updatedAt: number
}

export interface CaseDocument {
  id: string
  filename: string
  storage_path?: string
  download_url?: string
  content_type?: string
  file_size?: number
}

export interface Case {
  id: string
  name: string
  type: string
  status: 'pending' | 'processing' | 'complete' | 'error'
  date: string
  progress: number
  documents: CaseDocument[]
  flags: number
  summary?: CaseSummary
  incomeVerification?: IncomeVerification
  borrower: BorrowerInfo
  runtime?: CaseRuntime
  documentScan?: DocumentScanState
}

export interface CasesState {
  entities: Record<string, Case>
  ids: string[]
  selectedCaseId: string | null
  loading: boolean
  error: string | null
  runtimes: Record<string, CaseRuntime>
  viewingDocument: ViewingDocument | null
}

const initialState: CasesState = {
  entities: {},
  ids: [],
  selectedCaseId: null,
  loading: false,
  error: null,
  runtimes: {},
  viewingDocument: null,
}

// Helper function to extract classifications from runtime data
function extractClassificationsFromRuntime(runtime?: CaseRuntime): DocumentClassification[] | undefined {
  if (!runtime?.data) return undefined
  
  // Try frontend_data.classifications first
  if (runtime.data.frontend_data?.classifications?.length > 0) {
    return runtime.data.frontend_data.classifications
  }
  
  // Try summary.scan_documents.data.files
  if (runtime.data.summary?.scan_documents?.data?.files?.length > 0) {
    return runtime.data.summary.scan_documents.data.files
  }
  
  return undefined
}

const casesSlice = createSlice({
  name: 'cases',
  initialState,
  reducers: {
    addCase(state, action: PayloadAction<Case>) {
      // Only add incomeVerification if it's provided and complete
      // Don't create a fallback object - let it be undefined if missing
      // Ensure currentPhase is always valid (1-5)

      const { id, ...updates } = action.payload
      const isSelectedCase = state.selectedCaseId === id
      


      const incomeVerification = action.payload.incomeVerification 
        ? {
            ...action.payload.incomeVerification,
            currentPhase: action.payload.incomeVerification.currentPhase || 1
          }
        : { currentPhase: 1 } as any
      
      // Preserve existing documentScan.classifications if case already exists
      const existingCase = state.entities[action.payload.id]
      const existingClassifications = existingCase?.documentScan?.classifications || []
      
      // Extract classifications from runtime data if present
      const runtimeClassifications = extractClassificationsFromRuntime(action.payload.runtime)
      
      // Merge classifications: prefer runtime data, fallback to existing, then to provided documentScan
      let finalClassifications: DocumentClassification[] = []
      if (runtimeClassifications && runtimeClassifications.length > 0) {
        finalClassifications = runtimeClassifications
      } else if (existingClassifications.length > 0) {
        finalClassifications = existingClassifications
      } else if (action.payload.documentScan?.classifications && action.payload.documentScan.classifications.length > 0) {
        finalClassifications = action.payload.documentScan.classifications
      }
      
      // Build documentScan state, preserving existing classifications if no new ones found
      const documentScan = action.payload.documentScan ? {
        ...action.payload.documentScan,
        classifications: finalClassifications
      } : (existingCase?.documentScan ? {
        ...existingCase.documentScan,
        classifications: finalClassifications
      } : undefined)
      
      state.entities[action.payload.id] = { 
        ...action.payload, 
        incomeVerification,
        documentScan
      }
      if (!state.ids.includes(action.payload.id)) {
        state.ids.push(action.payload.id)
      }
    },

    updateCase(state, action: PayloadAction<Partial<Case> & { id: string }>) {
      const { id, ...updates } = action.payload
      const isSelectedCase = state.selectedCaseId === id
      
      
      if (state.entities[id]) {
        const oldCase = { ...state.entities[id] }
        const changes: Record<string, { from: any; to: any }> = {}
        
        // Track changes in top-level properties
        if (updates.status !== undefined && oldCase.status !== updates.status) {
          changes.status = { from: oldCase.status, to: updates.status }
        }
        if (updates.progress !== undefined && oldCase.progress !== updates.progress) {
          changes.progress = { from: oldCase.progress, to: updates.progress }
        }
        if (updates.documents !== undefined) {
          const oldDocsCount = oldCase.documents?.length || 0
          const newDocsCount = updates.documents?.length || 0
          if (oldDocsCount !== newDocsCount) {
            changes.documents = { from: `${oldDocsCount} docs`, to: `${newDocsCount} docs` }
          }
        }
        
        // Deep merge for nested objects like incomeVerification
        if (updates.incomeVerification) {
          const oldIncomeVerification = state.entities[id].incomeVerification
          console.log('📦 Before merge - old incomeVerification:', {
            currentPhase: oldIncomeVerification?.currentPhase,
            stableUIPhase: oldIncomeVerification?.stableUIPhase
          })
          
          state.entities[id].incomeVerification = {
            ...state.entities[id].incomeVerification,
            ...updates.incomeVerification
          }
          
          const newIncomeVerification = state.entities[id].incomeVerification
          if (oldIncomeVerification?.currentPhase !== newIncomeVerification?.currentPhase) {
            changes.currentPhase = {
              from: oldIncomeVerification?.currentPhase,
              to: newIncomeVerification?.currentPhase
            }
          }
          if (oldIncomeVerification?.stableUIPhase !== newIncomeVerification?.stableUIPhase) {
            changes.stableUIPhase = {
              from: oldIncomeVerification?.stableUIPhase,
              to: newIncomeVerification?.stableUIPhase
            }
          }
          
          console.log('✅ After merge - new incomeVerification:', {
            currentPhase: newIncomeVerification?.currentPhase,
            stableUIPhase: newIncomeVerification?.stableUIPhase
          })
          
          // Remove incomeVerification and documentScan from updates to avoid double-setting
          const { incomeVerification, documentScan, ...restUpdates } = updates
          
          Object.assign(state.entities[id], restUpdates)

        } else {
          // Extract documentScan from updates to prevent overwriting
          const { documentScan, ...restUpdates } = updates
          Object.assign(state.entities[id], restUpdates)
        }
        
        // Handle documentScan - preserve existing classifications and extract from runtime
        if (updates.documentScan || updates.runtime) {
          const oldScan = oldCase.documentScan
          const existingClassifications = oldScan?.classifications || []
          
          // Extract classifications from runtime data if present
          const runtimeClassifications = extractClassificationsFromRuntime(updates.runtime || oldCase.runtime)
          
          // Merge classifications: prefer runtime data, fallback to existing, then to provided documentScan
          // Only use provided documentScan.classifications if it's not empty
          let finalClassifications: DocumentClassification[] = []
          if (runtimeClassifications && runtimeClassifications.length > 0) {
            finalClassifications = runtimeClassifications
          } else if (updates.documentScan?.classifications && updates.documentScan.classifications.length > 0) {
            // Only use provided classifications if they're not empty
            finalClassifications = updates.documentScan.classifications
          } else if (existingClassifications.length > 0) {
            // Preserve existing classifications if no new ones provided
            finalClassifications = existingClassifications
          }
          
          // Update documentScan, preserving classifications
          if (updates.documentScan) {
            if (!state.entities[id].documentScan) {
              state.entities[id].documentScan = {
                status: 'idle',
                workflowId: null,
                classifications: [],
                complianceResult: null,
                progress: 0,
                error: null,
              }
            }
            // Merge documentScan updates but preserve classifications (don't override with empty array)
            const { classifications: _, ...documentScanUpdates } = updates.documentScan
            Object.assign(state.entities[id].documentScan!, documentScanUpdates)
            // Only update classifications if we have new ones
            if (finalClassifications.length > 0) {
              state.entities[id].documentScan!.classifications = finalClassifications
            }
            
            if (oldScan?.status !== state.entities[id].documentScan?.status) {
              changes.documentScanStatus = {
                from: oldScan?.status,
                to: state.entities[id].documentScan?.status
              }
            }
          } else if (runtimeClassifications && runtimeClassifications.length > 0) {
            // If runtime has classifications but documentScan wasn't updated, update it
            if (!state.entities[id].documentScan) {
              state.entities[id].documentScan = {
                status: 'idle',
                workflowId: null,
                classifications: [],
                complianceResult: null,
                progress: 0,
                error: null,
              }
            }
            state.entities[id].documentScan!.classifications = finalClassifications
          }
        }
        
        // Track runtime changes
        if (updates.runtime) {
          const oldRuntime = oldCase.runtime
          if (oldRuntime?.status !== updates.runtime?.status) {
            changes.runtimeStatus = { from: oldRuntime?.status, to: updates.runtime?.status }
          }
          if (oldRuntime?.progress !== updates.runtime?.progress) {
            changes.runtimeProgress = { from: oldRuntime?.progress, to: updates.runtime?.progress }
          }
        }
        
        // Log changes if this is the selected case
        if (isSelectedCase && Object.keys(changes).length > 0) {
          console.log('🔄 selectedCase updated via updateCase:', {
            caseId: id,
            changes,
            timestamp: new Date().toISOString()
          })
        }
      } else {
        console.error('❌ Case not found in Redux:', id)
      }
    },

    selectCase(state, action: PayloadAction<string | null>) {
      const previousCaseId = state.selectedCaseId
      const newCaseId = action.payload
      
      console.log('🔵 selectCase reducer called:', {
        from: previousCaseId,
        to: newCaseId,
        caseExists: newCaseId ? !!state.entities[newCaseId] : false,
        timestamp: new Date().toISOString()
      })
      
      if (newCaseId && state.entities[newCaseId]) {
        const caseData = state.entities[newCaseId]
        console.log('📋 Selected case data:', {
          caseId: caseData.id,
          status: caseData.status,
          progress: caseData.progress,
          documentsCount: caseData.documents?.length || 0,
          currentPhase: caseData.incomeVerification?.currentPhase,
          stableUIPhase: caseData.incomeVerification?.stableUIPhase,
          documentScanStatus: caseData.documentScan?.status,
          runtimeStatus: caseData.runtime?.status
        })
      }
      
      state.selectedCaseId = newCaseId
      state.viewingDocument = null
    },

    deleteCase(state, action: PayloadAction<string>) {
      const id = action.payload
      delete state.entities[id]
      const remainingIds = state.ids.filter(caseId => caseId !== id)
      state.ids = remainingIds
      if (state.selectedCaseId === id) {
        // Select the first remaining case if any, otherwise null
        state.selectedCaseId = remainingIds.length > 0 ? remainingIds[0] : null
      }
    },

    setLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload
    },

    setError(state, action: PayloadAction<string | null>) {
      state.error = action.payload
    },

    updateCaseRuntime(state, action: PayloadAction<{ caseId: string; runtime: CaseRuntime }>) {
      const { caseId, runtime } = action.payload
      const isSelectedCase = state.selectedCaseId === caseId
      const oldRuntime = state.entities[caseId]?.runtime
      
      state.runtimes[caseId] = runtime
      if (state.entities[caseId]) {
        state.entities[caseId].runtime = runtime
        // Also update progress if it's higher than current
        if (runtime.progress > state.entities[caseId].progress) {
          state.entities[caseId].progress = runtime.progress
        }
      }
      
      if (isSelectedCase) {
        const changes: Record<string, { from: any; to: any }> = {}
        if (oldRuntime?.status !== runtime.status) {
          changes.status = { from: oldRuntime?.status, to: runtime.status }
        }
        if (oldRuntime?.phase !== runtime.phase) {
          changes.phase = { from: oldRuntime?.phase, to: runtime.phase }
        }
        if (oldRuntime?.progress !== runtime.progress) {
          changes.progress = { from: oldRuntime?.progress, to: runtime.progress }
        }
        if (oldRuntime?.message !== runtime.message) {
          changes.message = { from: oldRuntime?.message, to: runtime.message }
        }
        
        if (Object.keys(changes).length > 0) {
          console.log('🔄 selectedCase runtime updated:', {
            caseId,
            changes,
            timestamp: new Date().toISOString()
          })
        }
      }
    },

    clearCaseRuntime(state, action: PayloadAction<string>) {
      const caseId = action.payload
      if (state.entities[caseId]) {
        delete state.entities[caseId].runtime
      }
    },

    updateCasePhase(state, action: PayloadAction<{ caseId: string; phase: IncomeVerificationPhase }>) {
      const { caseId, phase } = action.payload
      const caseEntity = state.entities[caseId]
      const isSelectedCase = state.selectedCaseId === caseId
      
      // Validate phase is in range 1-5
      if (phase < 1 || phase > 5) {
        console.error(`Invalid phase ${phase} for case ${caseId}, must be 1-5. Ignoring update.`)
        return
      }
      
      const oldPhase = caseEntity?.incomeVerification?.currentPhase
      
      if (caseEntity?.incomeVerification) {
        caseEntity.incomeVerification.currentPhase = phase
      } else if (caseEntity) {
        // Create incomeVerification object if it doesn't exist
        caseEntity.incomeVerification = { currentPhase: phase } as any
      }
      
      if (isSelectedCase && oldPhase !== phase) {
        console.log('🔄 selectedCase phase updated via updateCasePhase:', {
          caseId,
          from: oldPhase,
          to: phase,
          timestamp: new Date().toISOString()
        })
      }
    },

    selectDoc(state, action: PayloadAction<{ caseId: string; documentId: string; documentUrl?: string }>) {
      const { caseId, documentId, documentUrl } = action.payload
      state.selectedCaseId = caseId
      state.viewingDocument = { documentId, documentUrl }
    },

    clearViewingDocument(state) {
      state.viewingDocument = null
    },

    // Document scanning reducers
    updateDocumentScan(state, action: PayloadAction<{ caseId: string; scan: Partial<DocumentScanState> }>) {
      const { caseId, scan } = action.payload
      const caseEntity = state.entities[caseId]
      const isSelectedCase = state.selectedCaseId === caseId
      
      if (caseEntity) {
        const oldScan = caseEntity.documentScan ? { ...caseEntity.documentScan } : null
        
        if (!caseEntity.documentScan) {
          caseEntity.documentScan = {
            status: 'idle',
            workflowId: null,
            classifications: [],
            complianceResult: null,
            progress: 0,
            error: null,
          }
        }
        
        Object.assign(caseEntity.documentScan, scan)
        
        if (isSelectedCase) {
          const changes: Record<string, { from: any; to: any }> = {}
          if (oldScan?.status !== caseEntity.documentScan.status) {
            changes.status = { from: oldScan?.status, to: caseEntity.documentScan.status }
          }
          if (oldScan?.progress !== caseEntity.documentScan.progress) {
            changes.progress = { from: oldScan?.progress, to: caseEntity.documentScan.progress }
          }
          if (oldScan?.classifications?.length !== caseEntity.documentScan.classifications?.length) {
            changes.classifications = {
              from: `${oldScan?.classifications?.length || 0} items`,
              to: `${caseEntity.documentScan.classifications?.length || 0} items`
            }
          }
          
          if (Object.keys(changes).length > 0) {
            console.log('🔄 selectedCase documentScan updated:', {
              caseId,
              changes,
              timestamp: new Date().toISOString()
            })
          }
        }
      }
    },

    setDocumentScanStatus(state, action: PayloadAction<{ caseId: string; status: DocumentScanStatus; progress?: number }>) {
      const { caseId, status, progress } = action.payload
      const caseEntity = state.entities[caseId]
      
      if (caseEntity) {
        if (!caseEntity.documentScan) {
          caseEntity.documentScan = {
            status: 'idle',
            workflowId: null,
            classifications: [],
            complianceResult: null,
            progress: 0,
            error: null,
          }
        }
        
        // If currentPhase > 1, set status to 'approved'
        const finalStatus = (caseEntity.incomeVerification?.currentPhase ?? 0) > 1 
          ? 'approved' 
          : status
        
        
        caseEntity.documentScan.status = state.entities[caseId].documentScan.status === 'approved' ? 'approved' : finalStatus
        if (progress !== undefined) {
          caseEntity.documentScan.progress = progress
        }
      }
    },

    setDocumentClassifications(state, action: PayloadAction<{ caseId: string; classifications: DocumentClassification[]; complianceResult?: ComplianceResult | null }>) {
      const { caseId, classifications, complianceResult } = action.payload
      const caseEntity = state.entities[caseId]
      
      if (caseEntity) {
        if (!caseEntity.documentScan) {
          caseEntity.documentScan = {
            status: 'idle',
            workflowId: null,
            classifications: [],
            complianceResult: null,
            progress: 0,
            error: null,
          }
        }
        
        caseEntity.documentScan.classifications = classifications
        if (complianceResult !== undefined) {
          caseEntity.documentScan.complianceResult = complianceResult
        }
      }
    },

    clearDocumentScan(state, action: PayloadAction<string>) {
      const caseId = action.payload
      const caseEntity = state.entities[caseId]
      
      if (caseEntity) {
        caseEntity.documentScan = {
          status: 'idle',
          workflowId: null,
          classifications: [],
          complianceResult: null,
          progress: 0,
          error: null,
        }
      }
    }
  }
})

export const {
  addCase,
  updateCase,
  selectCase,
  deleteCase,
  setLoading,
  setError,
  updateCaseRuntime,
  clearCaseRuntime,
  updateCasePhase,
  selectDoc,
  clearViewingDocument,
  updateDocumentScan,
  setDocumentScanStatus,
  setDocumentClassifications,
  clearDocumentScan
} = casesSlice.actions

export default casesSlice.reducer
